import { lazy, Suspense, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import FullSizeLoading from "components/FullSizeLoading";
import useCancelOption from "hooks/api/useCancelOption";
import useCancelService from "hooks/api/useCancelService";
import useFindOption from "hooks/api/useFindOption";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";
import { useInfoModal, useWarningModal } from "hooks/useModal";
import { OptionDetail, RequestServiceProgress } from "pages/OptionDetail/constants";
import { isBrowser, isTablet } from "utils/deviceDetect";

const OptionDetailPresenter = lazy(() =>
  isBrowser
    ? import("pages/OptionDetail/pc/OptionDetailPresenter")
    : isTablet
      ? import("pages/OptionDetail/sp/OptionDetailPresenter")
      : import("pages/OptionDetail/sp/OptionDetailPresenter")
);

export type OptionDetailPresenterProps = {
  option: OptionDetail;
  optionDetailModalVisible: boolean;
  handleNavigateList: () => void;
  handleCancelOption: () => void;
  handleNavigateEstimate: (serviceId: string) => void;
  handleCancelService: (serviceId: string) => void;
  handleOpenDetailModal: (optionId: string) => void;
  handleCloseModal: () => void;
  genCancelableCondition: ({
    progress,
    isCanceled,
  }: {
    progress: RequestServiceProgress;
    isCanceled: boolean;
  }) => boolean;
};

const OptionDetailContainer = () => {
  const [infoModalState, setInfoModalState] = useState<{ title: string }>({ title: "" });
  const [warningModalState, setWarningModalState] = useState<{ title: string; label: string; onClick: () => void }>({
    title: "",
    label: "",
    onClick: () => null,
  });
  const [optionDetailModalVisible, setOptionDetailModalVisible] = useState(false);

  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const { cancelOption } = useCancelOption();
  const { cancelService } = useCancelService();
  const { showInfoModal, InfoModal } = useInfoModal();
  const { WarningModal, showWarningModal, hideWarningModal } = useWarningModal();
  const { showErrorModal } = useErrorModalStatusContext();
  const { option, mutate } = useFindOption(id);

  const genCancelableCondition = ({
    progress,
    isCanceled,
  }: {
    progress: RequestServiceProgress;
    isCanceled: boolean;
  }) => {
    return progress === "CHECKING" && !isCanceled;
  };

  const handleNavigateList = () => {
    navigate("/option");
  };
  const handleNavigateEstimate = (serviceId: string) => {
    navigate(`/option/${id}/service/${serviceId}`);
  };

  const openCompleteModal = () => {
    setInfoModalState({
      title: "依頼を停止しました",
    });
    showInfoModal();
  };

  const cancelOptionHandler = async () => {
    const status = await cancelOption(id).catch(() => {
      showErrorModal({ httpMethod: "put" });
      hideWarningModal();
    });
    if (status === 200) {
      hideWarningModal();
      openCompleteModal();
      await mutate();
    }
  };

  const cancelServiceHandler = async (serviceId: string) => {
    const status = await cancelService({
      optionId: id,
      serviceId,
    }).catch(() => {
      showErrorModal({ httpMethod: "put" });
      hideWarningModal();
    });
    if (status === 200) {
      hideWarningModal();
      openCompleteModal();
      await mutate();
    }
  };

  const openSubmitModal = (type: "option" | "service", serviceId?: string) => {
    setWarningModalState({
      title: "依頼を停止しますか？",
      label: "停止する",
      onClick: type === "option" ? () => cancelOptionHandler() : () => cancelServiceHandler(serviceId!),
    });
    showWarningModal();
  };

  const handleCancelOption = () => {
    openSubmitModal("option");
  };
  const handleCancelService = (serviceId: string) => {
    openSubmitModal("service", serviceId);
  };
  const handleOpenDetailModal = (optionId: string) => {
    setOptionDetailModalVisible(true);
  };
  const handleCloseModal = () => {
    setOptionDetailModalVisible(false);
  };

  return (
    <Suspense fallback={<FullSizeLoading />}>
      <OptionDetailPresenter
        option={option}
        optionDetailModalVisible={optionDetailModalVisible}
        handleNavigateList={handleNavigateList}
        handleNavigateEstimate={handleNavigateEstimate}
        handleCancelService={handleCancelService}
        handleCancelOption={handleCancelOption}
        genCancelableCondition={genCancelableCondition}
        handleOpenDetailModal={handleOpenDetailModal}
        handleCloseModal={handleCloseModal}
      />
      <WarningModal
        title={warningModalState.title}
        label={warningModalState.label}
        onClick={warningModalState.onClick}
      />
      <InfoModal title={infoModalState.title} />
    </Suspense>
  );
};

export default OptionDetailContainer;
